// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { supabase as mainSupabase } from '@/integrations/supabase/client';
import { getSessionUser } from '@/lib/authSession';

// Declare global type
declare global {
  interface Window {
    __RECOVERY_HASH__?: string;
  }
}

// Get hash from global (captured in index.html before React loaded)
const getRecoveryHash = () => {
  const globalHash = typeof window !== 'undefined' ? window.__RECOVERY_HASH__ : undefined;
  const currentHash = typeof window !== 'undefined' ? window.location.hash : '';
  console.log('🔐 RESET: Global hash:', globalHash ? 'present' : 'none');
  console.log('🔐 RESET: Current hash:', currentHash ? 'present' : 'none');
  return globalHash || currentHash || '';
};

// Parse tokens from hash
const parseHashTokens = (hash: string) => {
  if (!hash) return null;
  const params = new URLSearchParams(hash.substring(1));
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const type = params.get('type');
  console.log('🔐 PARSED - access:', !!accessToken, 'refresh:', !!refreshToken, 'type:', type);
  if (accessToken && refreshToken && type === 'recovery') {
    return { accessToken, refreshToken };
  }
  return null;
};

// Create dedicated supabase client with localStorage (works in incognito)
const resetSupabase = createClient(
  'https://pvaumvowzncvrdkkvnqq.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InB2YXVtdm93em5jdnJka2t2bnFxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDU5MzkwNzYsImV4cCI6MjA2MTUxNTA3Nn0.tzzUBAW0sotu9taBdFv62G3RYDAnP7C5CPluRo4Odcs',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'password-reset-session',
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    }
  }
);

export default function ResetPasswordPage() {
  console.log('🔐 ResetPasswordPage: Component mounted (NEW VERSION)');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidRecovery, setIsValidRecovery] = useState<boolean | null>(null);
  const hasAttemptedSessionSetup = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    let resolved = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const markValid = () => {
      if (resolved) return;
      resolved = true;
      if (timeoutId) clearTimeout(timeoutId);
      console.log('🔐 Recovery session detected — showing reset form');
      setIsValidRecovery(true);
      // Clean up any hash/global if present
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      delete window.__RECOVERY_HASH__;
    };

    // 1. Listen for the recovery auth event / any session becoming available.
    const { data: { subscription } } = mainSupabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔐 onAuthStateChange:', event, 'session:', !!session);
        if (event === 'PASSWORD_RECOVERY' || session) {
          markValid();
        }
      }
    );

    const init = async () => {
      // 2. Legacy fallback: if the link still delivers tokens in the hash,
      //    establish the session on the main client (do NOT redirect away on absence).
      if (!hasAttemptedSessionSetup.current) {
        hasAttemptedSessionSetup.current = true;
        const tokens = parseHashTokens(getRecoveryHash());
        if (tokens) {
          try {
            const { data, error } = await mainSupabase.auth.setSession({
              access_token: tokens.accessToken,
              refresh_token: tokens.refreshToken,
            });
            if (!error && data.session) {
              markValid();
              return;
            }
          } catch (err) {
            console.error('🔐 setSession exception:', err);
          }
        }
      }

      // 3. Check for an already-established session (the /verify flow sets one).
      const { data: { session } } = await mainSupabase.auth.getSession();
      if (session) {
        markValid();
        return;
      }

      // 4. Only show "invalid" after a short grace period for the event to fire.
      timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.log('🔐 No recovery session detected after wait');
          setIsValidRecovery(false);
        }
      }, 2500);
    };

    init();

    return () => {
      subscription.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password) {
      toast.error('Please enter a new password');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error } = await mainSupabase.auth.updateUser({
        password: password
      });

      if (error) {
        toast.error(error.message);
      } else {
        // Get current user
        const { data: { user } } = await getSessionUser(mainSupabase);
        
        if (user) {
          const now = new Date().toISOString();
          
          // 1. Update profiles.claimed_at
          const { error: profileError } = await mainSupabase
            .from('profiles')
            .update({ claimed_at: now })
            .eq('user_id', user.id);

          if (profileError) {
            console.error('Failed to update profiles.claimed_at:', profileError);
          } else {
            console.log('✅ profiles.claimed_at updated:', now);
          }

          // 2. Update athlete_import_records.password_set_at
          if (user.email) {
            const { error: importError } = await mainSupabase
              .from('athlete_import_records')
              .update({ password_set_at: now })
              .eq('athlete_email', user.email);

            if (importError) {
              console.error('Failed to update athlete_import_records.password_set_at:', importError);
            } else {
              console.log('✅ athlete_import_records.password_set_at updated:', now);
            }
          }

          // Notify admins if this came from welcome email campaign
          const urlParams = new URLSearchParams(window.location.search);
          const utmCampaign = urlParams.get('utm_campaign');
          const utmSource = urlParams.get('utm_source');
          
          if (utmCampaign === 'athlete_migration' || utmSource === 'welcome_campaign') {
            try {
              await mainSupabase.functions.invoke('notify-admin-password-reset', {
                body: { 
                  userEmail: user.email,
                  userName: user.user_metadata?.name || 'Unknown',
                  timestamp: now
                }
              });
            } catch (notifyError) {
              console.error('Failed to notify admins:', notifyError);
            }
          }
        }
        
        // Transfer session to main app's supabase client
        const { data: sessionData } = await mainSupabase.auth.getSession();
        if (sessionData.session) {
          console.log('🔐 Transferring session to main client...');
          await mainSupabase.auth.setSession({
            access_token: sessionData.session.access_token,
            refresh_token: sessionData.session.refresh_token,
          });
        }
        
        toast.success('Password updated successfully!');
        // Use hard redirect to ensure AuthContext reinitializes with fresh session
        // navigate() doesn't work here because AuthContext skips profile fetch on reset pages
        window.location.href = '/dashboard';
        return; // Prevent further execution
      }
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking recovery status
  if (isValidRecovery === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Verifying reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Genuinely no session / recovery event after the grace period
  if (isValidRecovery === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Invalid or expired reset link</CardTitle>
            <CardDescription>
              This password reset link is no longer valid. Please request a new one.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/auth/forgot-password')}>
              Request a new link
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }


  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
          <CardDescription>
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}